import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiFetch } from '../api/client';
import type {
  CrmEvidenceBodyResponse,
  DealDetailPayload,
  DealDismissReason,
  DealMineTeam,
  DealStatusChip,
  DealsListResponse,
  FieldDismissReason,
} from '../types';

// ============================================================================
// Phase B — Deals tab hooks
// ============================================================================
// Reads and mutates the Deals tab API surface (B-2a + B-2b backend).
// Path note: GET endpoints live under /api/deals/, mutations under
// /api/deal-cards/ to avoid path collision with Phase A's int-typed
// /api/deals/{opportunity_id}/dismiss. The backend router has a comment
// explaining the constraint; the frontend just respects it.

export interface DealsListFilters {
  chip?: DealStatusChip;
  mine_team?: DealMineTeam;
  search?: string;
  limit?: number;
  // Slice J §1 — multi-select user narrow. Emits one `?user=<email>` per
  // entry. Combines with mine_team.
  user_filter?: string[];
}

export function useDeals(filters: DealsListFilters = {}) {
  const params = new URLSearchParams();
  if (filters.chip) params.set('chip', filters.chip);
  if (filters.mine_team) params.set('mine_team', filters.mine_team);
  if (filters.search) params.set('search', filters.search);
  if (filters.limit != null) params.set('limit', String(filters.limit));
  if (filters.user_filter && filters.user_filter.length > 0) {
    for (const u of filters.user_filter) params.append('user', u);
  }
  const qs = params.toString();
  return useQuery<DealsListResponse>({
    queryKey: ['deals', 'list', filters],
    queryFn: () => apiFetch<DealsListResponse>(`/api/deals${qs ? `?${qs}` : ''}`),
    // The list refreshes on its own when mutations invalidate it; long
    // stale time keeps the tab snappy while the user explores cards.
    staleTime: 30_000,
    // Poll only while a card is mid re-aggregation (e.g. just after a merge),
    // so the "aggregating…" indicator clears on its own; otherwise no polling.
    refetchInterval: (query) =>
      query.state.data?.deals?.some((d) => d.needs_aggregation) ? 4000 : false,
  });
}

/**
 * Pillar F.5 — ONE evidence row's body on the Deals tab, fetched on the operator's click.
 *
 * The card payload carries no body text any more (only `body_available` per row), so this is the only
 * route to it. `enabled` is the whole point: true only after a click — never on render, never on hover.
 * `retry: false` because every refusal is a 404 and re-asking cannot change the answer.
 */
export function useDealEvidenceBody(
  dealKey: string | null, eventLogId: number | null, enabled: boolean,
) {
  return useQuery<CrmEvidenceBodyResponse>({
    queryKey: ['deals', 'evidence-body', dealKey, eventLogId],
    queryFn: () => apiFetch<CrmEvidenceBodyResponse>(
      `/api/deal-cards/${encodeURIComponent(dealKey!)}/evidence/${eventLogId}/body`),
    enabled: enabled && !!dealKey && eventLogId != null,
    // F.5 review finding 6 — NO stale window on a body. `staleTime` here meant a deliberate re-open
    // within five minutes served the cached text and wrote NO audit row, so "audited per read" was
    // really "audited per fetch" — and the audit trail is half of why the read moved to the click.
    // A body is <=2,000 chars; the refetch is trivial next to the attribution being correct.
    staleTime: 0,
    retry: false,
  });
}

export function useDealDetail(dealKey: string | null) {
  return useQuery<DealDetailPayload>({
    queryKey: ['deals', 'detail', dealKey],
    queryFn: () => apiFetch<DealDetailPayload>(`/api/deals/${encodeURIComponent(dealKey!)}`),
    enabled: !!dealKey,
    staleTime: 15_000,
    // Poll while this card is mid re-aggregation (the legacy "owed, no job
    // yet" flag) OR a Phase B job is queued/running for it (queue-only Phase
    // B), so the pending chip clears and the fresh proposal lands without a
    // manual refresh.
    refetchInterval: (query) =>
      query.state.data?.needs_aggregation || query.state.data?.phase_b_job != null
        ? 4000
        : false,
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Mutations
// ─────────────────────────────────────────────────────────────────────────────

interface DismissDealArgs {
  dealKey: string;
  reason: DealDismissReason;
  note?: string;
}

export function useDismissDeal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ dealKey, reason, note }: DismissDealArgs) =>
      apiFetch<{ status: string; dismiss_id: number }>(
        `/api/deal-cards/${encodeURIComponent(dealKey)}/dismiss`,
        { method: 'POST', body: JSON.stringify({ reason, note }) },
      ),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['deals'] });
      qc.invalidateQueries({ queryKey: ['deals', 'detail', vars.dealKey] });
    },
  });
}

interface DismissFieldArgs {
  dealKey: string;
  fieldName: string;
  reason: FieldDismissReason;
  note?: string;
  // field-UX-refinements spec §10.4 — echoed so the server can 409 if the
  // card went stale (a newer suggestion landed) before this action.
  reviewSuggestionId?: number | null;
}

export function useDismissField() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ dealKey, fieldName, reason, note, reviewSuggestionId }: DismissFieldArgs) =>
      apiFetch<{ status: string; dismiss_id: number }>(
        `/api/deal-cards/${encodeURIComponent(dealKey)}/fields/${encodeURIComponent(fieldName)}/dismiss`,
        { method: 'POST', body: JSON.stringify({ reason, note, review_suggestion_id: reviewSuggestionId ?? null }) },
      ),
    // onSettled (not onSuccess) so a stale-card 409 also refetches the detail,
    // pulling the operator onto the fresh card for re-review (spec §10.4).
    onSettled: (_data, _err, vars) => {
      qc.invalidateQueries({ queryKey: ['deals', 'detail', vars.dealKey] });
      qc.invalidateQueries({ queryKey: ['deals', 'list'] });
    },
  });
}

interface FieldKey {
  dealKey: string;
  fieldName: string;
  reviewSuggestionId?: number | null;
}

export function useAcceptField() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ dealKey, fieldName, reviewSuggestionId }: FieldKey) =>
      apiFetch<{ status: string }>(
        `/api/deal-cards/${encodeURIComponent(dealKey)}/fields/${encodeURIComponent(fieldName)}/accept`,
        { method: 'POST', body: JSON.stringify({ review_suggestion_id: reviewSuggestionId ?? null }) },
      ),
    onSettled: (_data, _err, vars) => {
      qc.invalidateQueries({ queryKey: ['deals', 'detail', vars.dealKey] });
      qc.invalidateQueries({ queryKey: ['deals', 'list'] });
    },
  });
}

/** Restore a dismissed field suggestion (field-UX-refinements spec §10.6). */
export function useRestoreField() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ dealKey, fieldName, reviewSuggestionId }: FieldKey) =>
      apiFetch<{ status: string; restore_id: number }>(
        `/api/deal-cards/${encodeURIComponent(dealKey)}/fields/${encodeURIComponent(fieldName)}/restore`,
        { method: 'POST', body: JSON.stringify({ review_suggestion_id: reviewSuggestionId ?? null }) },
      ),
    onSettled: (_data, _err, vars) => {
      qc.invalidateQueries({ queryKey: ['deals', 'detail', vars.dealKey] });
      qc.invalidateQueries({ queryKey: ['deals', 'list'] });
    },
  });
}

interface EditFieldArgs extends FieldKey {
  value: unknown;
}

export function applyFieldEditOptimistic(
  detail: DealDetailPayload | undefined,
  fieldName: string,
  value: unknown,
): DealDetailPayload | undefined {
  if (!detail) return detail;
  let changed = false;
  const fields = detail.fields.map((field) => {
    if (field.field_name !== fieldName) return field;
    changed = true;
    return { ...field, proposed_value: value };
  });
  return changed ? { ...detail, fields } : detail;
}

export function useEditField() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ dealKey, fieldName, value, reviewSuggestionId }: EditFieldArgs) =>
      apiFetch<{ status: string }>(
        `/api/deal-cards/${encodeURIComponent(dealKey)}/fields/${encodeURIComponent(fieldName)}/edit`,
        { method: 'POST', body: JSON.stringify({ value, review_suggestion_id: reviewSuggestionId ?? null }) },
      ),
    onMutate: async (vars) => {
      const detailKey = ['deals', 'detail', vars.dealKey] as const;
      await qc.cancelQueries({ queryKey: detailKey });
      const previousDetail = qc.getQueryData<DealDetailPayload>(detailKey);
      qc.setQueryData<DealDetailPayload>(detailKey, (old) =>
        applyFieldEditOptimistic(old, vars.fieldName, vars.value),
      );
      return { detailKey, previousDetail };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previousDetail) {
        qc.setQueryData(ctx.detailKey, ctx.previousDetail);
      }
    },
    onSettled: (_data, _err, vars) => {
      qc.invalidateQueries({ queryKey: ['deals', 'detail', vars.dealKey] });
      qc.invalidateQueries({ queryKey: ['deals', 'list'] });
    },
  });
}

export function useSetCustomInstructions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ dealKey, text }: { dealKey: string; text: string | null }) =>
      apiFetch<{ status: string }>(
        `/api/deal-cards/${encodeURIComponent(dealKey)}/custom-instructions`,
        { method: 'PUT', body: JSON.stringify({ text }) },
      ),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['deals', 'detail', vars.dealKey] });
    },
  });
}

export function useSetDealRecentUpdates() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      dealKey,
      progressHtml,
      newInfoHtml,
      reviewSuggestionId,
    }: {
      dealKey: string;
      progressHtml: string | null;
      newInfoHtml: string | null;
      reviewSuggestionId: number | null;
    }) =>
      apiFetch<{ status: string }>(
        `/api/deal-cards/${encodeURIComponent(dealKey)}/recent-updates`,
        {
          method: 'PUT',
          body: JSON.stringify({
            progress_html: progressHtml,
            new_info_html: newInfoHtml,
            // Mandatory stale-card guard — the server 409s if a newer
            // suggestion landed since this card rendered (per-log-cycle write).
            review_suggestion_id: reviewSuggestionId,
          }),
        },
      ),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['deals', 'detail', vars.dealKey] });
    },
  });
}

export function useSuppressRecentUpdatesBlock() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      dealKey,
      block,
      suppressed,
      reviewSuggestionId,
    }: {
      dealKey: string;
      block: 'progress' | 'new_info';
      suppressed: boolean;
      reviewSuggestionId: number | null;
    }) =>
      apiFetch<{ status: string }>(
        `/api/deal-cards/${encodeURIComponent(dealKey)}/recent-updates/${block}/suppress`,
        {
          method: 'POST',
          body: JSON.stringify({
            suppressed,
            review_suggestion_id: reviewSuggestionId,
          }),
        },
      ),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['deals', 'detail', vars.dealKey] });
    },
  });
}


// ─── Affinity write-back ────────────────────────────────────────────────────

export interface LogResponse {
  status: string;
  affinity_opp_id: number | null;
  fields_pushed: string[];
  field_errors: Record<string, string>;
  note_id: number | null;
  note_status: 'posted' | 'skipped' | 'failed';
  partial_warnings: string[];
}

/**
 * Log Deal (candidates) / Push Updates (Affinity opps). Server picks
 * the right action by deal_kind. Returns the new opp_id on Log Deal
 * or the same opp_id on Push.
 */
export function useLogDeal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ dealKey, reviewSuggestionId }: { dealKey: string; reviewSuggestionId?: number | null }) =>
      apiFetch<LogResponse>(
        `/api/deal-cards/${encodeURIComponent(dealKey)}/log`,
        { method: 'POST', body: JSON.stringify({ review_suggestion_id: reviewSuggestionId ?? null }) },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['deals'] });
    },
  });
}

// Slice J §J2 — bulk endpoints. Per-deal result is surfaced to the FE
// so partial failures render correctly.

export interface BulkItemResult {
  deal_key: string;
  status: 'ok' | 'error';
  error: string | null;
  affinity_opp_id?: number | null;
  fields_pushed?: string[];
  field_errors?: Record<string, string>;
  note_id?: number | null;
  note_status?: 'posted' | 'skipped' | 'failed';
  partial_warnings?: string[];
}

export interface BulkResponse {
  results: BulkItemResult[];
  ok_count: number;
  error_count: number;
}

export function useBulkDismiss() {
  const qc = useQueryClient();
  return useMutation<
    BulkResponse,
    Error,
    { deal_keys: string[]; reason: DealDismissReason; note?: string }
  >({
    mutationFn: (body) =>
      apiFetch<BulkResponse>('/api/deal-cards/bulk-dismiss', {
        method: 'POST',
        body: JSON.stringify({
          deal_keys: body.deal_keys,
          reason: body.reason,
          note: body.note ?? null,
        }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['deals'] });
    },
  });
}

export function useBulkLogPass() {
  const qc = useQueryClient();
  return useMutation<
    BulkResponse,
    Error,
    { deal_keys: string[]; category?: string; note?: string }
  >({
    mutationFn: (body) =>
      apiFetch<BulkResponse>('/api/deal-cards/bulk-log-pass', {
        method: 'POST',
        body: JSON.stringify({
          deal_keys: body.deal_keys,
          category: body.category ?? null,
          note: body.note ?? null,
        }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['deals'] });
    },
  });
}

export function useBulkPush() {
  const qc = useQueryClient();
  return useMutation<BulkResponse, Error, { deal_keys: string[] }>({
    mutationFn: (body) =>
      apiFetch<BulkResponse>('/api/deal-cards/bulk-push', {
        method: 'POST',
        body: JSON.stringify({ deal_keys: body.deal_keys }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['deals'] });
    },
  });
}

/**
 * Explicit pass terminal. Records the dismissal with reason='pass_terminal';
 * the aggregator suppresses re-proposals on the same evidence after that.
 * Slice J §J4 — `category` is the Affinity Lost Passed Category dropdown
 * value. Sentinel "Other" parks the note in lost_passed_notes only.
 */
export function useLogPass() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      dealKey,
      note,
      category,
    }: {
      dealKey: string;
      note?: string;
      category?: string;
    }) =>
      apiFetch<{ status: string; audit_id: number }>(
        `/api/deal-cards/${encodeURIComponent(dealKey)}/log-pass`,
        {
          method: 'POST',
          body: JSON.stringify({
            note: note ?? null,
            category: category ?? null,
          }),
        },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['deals'] });
    },
  });
}


// ─── Admin operational endpoints ───────────────────────────────────────────

export function useAttachToOpp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ dealKey, affinityOppId }: { dealKey: string; affinityOppId: number }) =>
      apiFetch<{ status: string; candidate_uuid: string; affinity_opp_id: number }>(
        `/api/deal-cards/${encodeURIComponent(dealKey)}/attach-to-opp`,
        {
          method: 'POST',
          body: JSON.stringify({ affinity_opp_id: affinityOppId }),
        },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['deals'] }),
  });
}

export interface UnbindEventArgs {
  dealKey: string;
  eventLogId: number;
  reason: string;
  rebindDealKind?: 'candidate' | 'affinity_opp';
  rebindDealKey?: string;
}

export interface UnbindEventResponse {
  status: string;
  audit_id: number;
  // Queue-only Phase B — one entry per re-aggregated deal (source + dedup
  // siblings + rebind target). `status` is always "queued" now; `job_id` is
  // the row a caller can point an operator at (dedupe means several deals
  // can share one job).
  aggregated: Array<{ deal_kind: string; deal_key: string; status: string; job_id?: number }>;
  auto_dismissed: Array<{ deal_key: string; dismiss_event_id: number }>;
}

export function useUnbindEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: UnbindEventArgs) =>
      apiFetch<UnbindEventResponse>(
        `/api/deal-cards/${encodeURIComponent(args.dealKey)}/unbind-event/${args.eventLogId}`,
        {
          method: 'POST',
          body: JSON.stringify({
            reason: args.reason,
            rebind_deal_kind: args.rebindDealKind ?? null,
            rebind_deal_key: args.rebindDealKey ?? null,
          }),
        },
      ),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['deals', 'detail', vars.dealKey] });
      qc.invalidateQueries({ queryKey: ['deals', 'list'] });
    },
  });
}


// ── Deal picker search ────────────────────────────────────────────────────
//
// Powers the "move evidence to a different deal" picker in the refute
// modal. Unified search across candidate_deals + opportunities snapshot.

export interface DealPickerResult {
  kind: 'candidate' | 'affinity_opp';
  deal_key: string;
  label: string;
  sublabel: string | null;
  stage: string | null;
}

/**
 * A committed native IOT deal with NO legacy alias, keyed by `crm_list_entry.id`.
 *
 * Its own type, and the union above stays narrow, ON PURPOSE: every endpoint a picked deal is
 * handed to — `merge-into`, the refute rebind, the NDA bind — validates
 * `^(candidate|affinity_opp)$`, so a `list_entry` flowing into one of those call sites is a 422.
 * Keeping the kinds apart makes that a compile error rather than a runtime one. Widening the
 * consumers is its own slice; until then this arm is carried by the API and rendered by nobody.
 */
export interface NativeDealPickerResult extends Omit<DealPickerResult, 'kind'> {
  kind: 'list_entry';
}

export interface DealPickerResponse {
  candidates: DealPickerResult[];
  affinity_opps: DealPickerResult[];
  /** Optional so an older response shape still type-checks. */
  native_deals?: NativeDealPickerResult[];
}

/**
 * The deal picker's search. **A BLANK query is a real query**, not a disabled one: the endpoint
 * returns the most recent deals so the picker opens with something to choose from rather than an
 * instruction to start typing (owner, 2026-08-05). Terminal-stage deals are included and ranked last;
 * every result carries its stage, so a lost deal is visibly lost at the point of choosing it.
 */
export function useDealPickerSearch(query: string, enabled: boolean = true) {
  const trimmed = query.trim();
  return useQuery<DealPickerResponse>({
    queryKey: ['deal-picker-search', trimmed],
    queryFn: () =>
      apiFetch<DealPickerResponse>(
        `/api/deal-cards/search?q=${encodeURIComponent(trimmed)}&limit=8`,
      ),
    enabled,
    staleTime: 30 * 1000,
  });
}

// Queue-only Phase B (owner ruling 2026-08-20) — the aggregator no longer runs inline on this
// request; it enqueues a `maintenance_jobs` row for the scheduler-owning process to drain.
export interface AggregateNowResponse {
  status: string;
  job_id: number;
  deduped: boolean;
  deal_kind: string;
  deal_key: string;
}

export function useAggregateNow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ dealKey }: { dealKey: string }) =>
      apiFetch<AggregateNowResponse>(
        `/api/deal-cards/${encodeURIComponent(dealKey)}/aggregate-now`,
        { method: 'POST' },
      ),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['deals', 'detail', vars.dealKey] });
    },
  });
}


// Wave B #10 — operator-driven merge of one deal into another. Source
// is `dealKey` (the card the operator is on); target is supplied via
// the picker.

export interface MergeIntoResponse {
  status: string;
  source_deal_kind: string;
  source_deal_key: string;
  target_deal_kind: string;
  target_deal_key: string;
  events_rebound: number;
  errors: Array<{ event_log_id: number | null; error: string }>;
  aggregated: Array<{ deal_kind: string; deal_key: string; status: string }>;
  // Queue-only Phase B — the job enqueued for the merge TARGET's
  // re-aggregation, or null only on an outright merge failure (no events
  // rebound). The FE's post-merge confirmation names this real work.
  aggregate_job_id: number | null;
}

export function useMergeDealInto() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      dealKey, targetDealKind, targetDealKey,
    }: {
      dealKey: string;
      targetDealKind: 'candidate' | 'affinity_opp';
      targetDealKey: string;
    }) =>
      apiFetch<MergeIntoResponse>(
        `/api/deal-cards/${encodeURIComponent(dealKey)}/merge-into`,
        {
          method: 'POST',
          body: JSON.stringify({
            target_deal_kind: targetDealKind,
            target_deal_key: targetDealKey,
          }),
        },
      ),
    onSuccess: (_data, vars) => {
      // Source is dismissed → list refresh; target inherited evidence
      // → its detail invalidates too.
      qc.invalidateQueries({ queryKey: ['deals', 'list'] });
      qc.invalidateQueries({ queryKey: ['deals', 'detail', vars.dealKey] });
      qc.invalidateQueries({
        queryKey: ['deals', 'detail', vars.targetDealKey],
      });
    },
  });
}
